/**
 * Gallery video review — GAL-12.
 *
 * The four film designs (20 Review Room, 21 Premiere, 22 Dailies,
 * 23 Story Reel) turn a gallery's films into a review room: notes
 * pinned to a timecode (resolve / reply), a per-film verdict
 * (approve / request changes), "select for final" and reactions.
 *
 * Same shape as galleryProofing.ts after SEC-1: visitors READ through
 * RLS (public + proofing-enabled galleries) and WRITE only through
 * SECURITY DEFINER RPCs that re-check the gallery is open. Identity is
 * the same per-browser picker id / name the favorites use, so a
 * client's notes and hearts belong to one person in the studio's
 * Proofing view.
 *
 * Demo galleries never reach here — the engine keeps demo/locked
 * review state in memory (see useGalleryEngine).
 *
 * Migration: supabase/migrations/20260905130000_gal12_video_review.sql
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
// SEC-PORTAL-2 — portal READS go through a session-less client so a
// visitor who has their own Orbit account isn't refused by the
// anon-only policies. See integrations/supabase/portal.ts.
import { portalReads } from '@/integrations/supabase/portal';
import { isDemoId } from '@/lib/demoScope';
import { getPickerIdentifier, getPickerName } from '@/services/galleryProofing';

export type VideoReviewStatus = 'review' | 'approved' | 'changes';

export interface GalleryVideoNoteRow {
  id: string;
  gallery_id: string;
  video_id: string;
  author_identifier: string;
  author_name: string | null;
  timecode_seconds: number | string;
  body: string;
  resolved: boolean;
  parent_id: string | null;
  /** Written by the studio from the editor's Review panel (GAL-12 notify). */
  from_studio?: boolean;
  created_at: string;
  updated_at: string;
}

export interface GalleryVideoReviewRow {
  id: string;
  gallery_id: string;
  video_id: string;
  reviewer_identifier: string;
  reviewer_name: string | null;
  status: VideoReviewStatus;
  selected: boolean;
  reactions: number;
  created_at: string;
  updated_at: string;
}

const notesKey = (galleryId: string | undefined) => ['gallery-video-notes', galleryId] as const;
const reviewsKey = (galleryId: string | undefined) => ['gallery-video-reviews', galleryId] as const;

// Cast through `any` — types.ts isn't regenerated for GAL-12 yet
// (same pattern as the SEC-1 RPCs in galleryProofing).
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = supabase as any;

// ─── Read ────────────────────────────────────────────────────────

/** Every note on every film in a gallery (visitor + studio). */
export const useGalleryVideoNotes = (galleryId: string | undefined) =>
  useQuery({
    queryKey: notesKey(galleryId),
    queryFn: async () => {
      if (!galleryId) return [];
      const { data, error } = await (portalReads() as any)
        .from('gallery_video_notes')
        .select('*')
        .eq('gallery_id', galleryId)
        .order('timecode_seconds', { ascending: true })
        .order('created_at', { ascending: true });
      if (error) throw error;
      return (data ?? []) as GalleryVideoNoteRow[];
    },
    enabled: !!galleryId && !isDemoId(galleryId),
  });

/** Every reviewer's verdict / selection / reactions per film. */
export const useGalleryVideoReviews = (galleryId: string | undefined) =>
  useQuery({
    queryKey: reviewsKey(galleryId),
    queryFn: async () => {
      if (!galleryId) return [];
      const { data, error } = await (portalReads() as any)
        .from('gallery_video_reviews')
        .select('*')
        .eq('gallery_id', galleryId);
      if (error) throw error;
      return (data ?? []) as GalleryVideoReviewRow[];
    },
    enabled: !!galleryId && !isDemoId(galleryId),
  });

// ─── Write (visitor side, through the guarded RPCs) ──────────────

export const useAddVideoNote = (galleryId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      videoId,
      seconds,
      body,
      parentId,
    }: {
      videoId: string;
      seconds: number;
      body: string;
      parentId?: string | null;
    }) => {
      const { data, error } = await db.rpc('public_gallery_add_video_note', {
        p_gallery_id: galleryId,
        p_video_id: videoId,
        p_author_identifier: getPickerIdentifier(),
        p_author_name: getPickerName(),
        p_seconds: Math.max(0, Number(seconds.toFixed(3))),
        p_body: body.trim(),
        p_parent_id: parentId ?? null,
      });
      if (error) throw error;
      return data as string;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: notesKey(galleryId) });
    },
  });
};

export const useSetVideoNoteResolved = (galleryId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ noteId, resolved }: { noteId: string; resolved: boolean }) => {
      const { error } = await db.rpc('public_gallery_set_video_note_resolved', {
        p_note_id: noteId,
        p_resolved: resolved,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: notesKey(galleryId) });
    },
  });
};

/**
 * Upsert the current viewer's review row for a film. Pass only the
 * fields that change: `status` (verdict), `selected`, or a
 * `reactDelta` (+1 per heart). Omitted fields keep their value.
 */
export const useSetVideoReview = (galleryId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      videoId,
      status,
      selected,
      reactDelta,
    }: {
      videoId: string;
      status?: VideoReviewStatus;
      selected?: boolean;
      reactDelta?: number;
    }) => {
      const { data, error } = await db.rpc('public_gallery_set_video_review', {
        p_gallery_id: galleryId,
        p_video_id: videoId,
        p_reviewer_identifier: getPickerIdentifier(),
        p_reviewer_name: getPickerName(),
        p_status: status ?? null,
        p_selected: selected ?? null,
        p_react_delta: reactDelta ?? 0,
      });
      if (error) throw error;
      return data as GalleryVideoReviewRow;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: reviewsKey(galleryId) });
    },
  });
};

// ─── Studio side ─────────────────────────────────────────────────

/**
 * Live updates for the editor's film review panel — same shape as
 * subscribeToProofingChanges. Both tables are in the realtime
 * publication (migration 20260907110000).
 */
export function subscribeToFilmReviewChanges(galleryId: string, onChange: () => void) {
  if (isDemoId(galleryId)) return () => {};
  // SEC-PORTAL-2 — same client as the reads above (see galleryProofing).
  const reads = portalReads();
  const channel = reads
    .channel(`gallery-film-review-${galleryId}`)
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'gallery_video_notes', filter: `gallery_id=eq.${galleryId}` },
      onChange,
    )
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'gallery_video_reviews', filter: `gallery_id=eq.${galleryId}` },
      onChange,
    )
    .subscribe();
  return () => {
    reads.removeChannel(channel);
  };
}

/**
 * Studio replies on a film — a note the client sees in the design's
 * notes list, flagged from_studio so the notify trigger stays quiet.
 * Writes through RLS (org members manage their galleries' notes).
 */
export const useStudioReplyVideoNote = (galleryId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      videoId,
      seconds,
      body,
      parentId,
      authorId,
      authorName,
    }: {
      videoId: string;
      seconds: number;
      body: string;
      parentId?: string | null;
      authorId: string;
      authorName: string;
    }) => {
      const { error } = await db.from('gallery_video_notes').insert({
        gallery_id: galleryId,
        video_id: videoId,
        author_identifier: `studio:${authorId}`,
        author_name: authorName,
        timecode_seconds: Math.max(0, Number(seconds.toFixed(3))),
        body: body.trim(),
        parent_id: parentId ?? null,
        from_studio: true,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: notesKey(galleryId) });
    },
  });
};

/** Studio resolves / reopens a client's note (RLS: own org). */
export const useStudioSetVideoNoteResolved = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ noteId, resolved }: { noteId: string; resolved: boolean }) => {
      const { error } = await db.from('gallery_video_notes').update({ resolved }).eq('id', noteId);
      if (error) throw error;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['gallery-video-notes'] });
    },
  });
};
